import React from "react";
import { PrimaryButton, GhostButton, Card } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { Warning, ArrowSquareOut, Robot } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const Stat = ({ label, value, cls, testid }) => (
  <div className="border border-white/10 p-2 text-center">
    <div className={`font-heading text-2xl ${cls}`} data-testid={testid}>{value ?? 0}</div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 mt-0.5">{label}</div>
  </div>
);

export default function DueRunModal({ open, result, onClose }) {
  const nav = useNavigate();
  if (!open || !result) return null;
  const r = result;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" data-testid="due-run-modal">
      <Card className="max-w-2xl w-full max-h-[88vh] overflow-y-auto p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 flex items-center gap-1">
              <Robot size={12} /> Discovery · Scheduled TED
            </div>
            <h2 className="font-heading text-xl tracking-tighter">Run Due Searches · Result</h2>
            <div className="text-[11px] text-zinc-500 mt-1">Triggered by {r.triggered_by || "—"}</div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="due-run-close">×</button>
        </div>

        {r.already_running ? (
          <div className="border border-[#FFB300]/30 bg-[#FFB300]/[0.06] p-3 text-[12px] text-[#FFB300]" data-testid="due-already-running">
            A due-search cycle is already running. Please wait for it to finish before triggering again.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4" data-testid="due-run-counts">
              <Stat label="Checked" value={r.rules_checked} cls="text-zinc-200" testid="due-stat-checked" />
              <Stat label="Run" value={r.rules_run} cls="text-zinc-200" testid="due-stat-run" />
              <Stat label="Created" value={r.candidates_created} cls="text-[#00E676]" testid="due-stat-created" />
              <Stat label="Possible Dups" value={r.possible_duplicates_created} cls="text-[#FFB300]" testid="due-stat-possible" />
              <Stat label="Exact Skipped" value={r.exact_duplicates_skipped} cls="text-zinc-400" testid="due-stat-skipped" />
              <Stat label="Failures" value={r.failures} cls={r.failures ? "text-[#FF3B30]" : "text-zinc-500"} testid="due-stat-failures" />
            </div>

            {Array.isArray(r.run_results) && r.run_results.length > 0 && (
              <div className="mb-4" data-testid="due-run-results">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Rules run</div>
                <div className="border border-white/10 divide-y divide-white/5">
                  {r.run_results.map((rr) => (
                    <div key={rr.run_id} className="flex items-center justify-between px-3 py-2 text-[11px]" data-testid={`due-result-${rr.rule_id}`}>
                      <span className="text-zinc-200 truncate mr-2">{rr.rule_name}</span>
                      <span className="flex items-center gap-3 shrink-0">
                        <span className="text-zinc-400">+{rr.candidates_created} new</span>
                        <span className="text-[#FFB300]">{rr.possible_duplicates_created} poss</span>
                        <span className="text-zinc-500">{rr.exact_duplicates_skipped} skip</span>
                        <StatusPill value={rr.run_status} />
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(r.warnings) && r.warnings.length > 0 && (
              <div className="mb-4 border border-[#FFB300]/30 bg-[#FFB300]/[0.06] p-3" data-testid="due-warnings">
                <div className="text-[10px] uppercase tracking-widest text-[#FFB300] mb-1.5 flex items-center gap-1">
                  <Warning size={12} /> Warnings
                </div>
                <ul className="space-y-1 text-[11px] text-zinc-300">
                  {r.warnings.map((w, i) => (
                    <li key={i} className="border-l-2 border-[#FFB300] pl-2" data-testid={`due-warning-${i}`}>{w}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap justify-end gap-2 mt-2">
          <GhostButton onClick={() => nav("/candidates")} data-testid="due-view-queue-btn">
            <ArrowSquareOut size={11} className="inline mr-1" /> Candidate Review Queue
          </GhostButton>
          <GhostButton onClick={() => nav("/search-runs")} data-testid="due-view-runs-btn">
            <ArrowSquareOut size={11} className="inline mr-1" /> Search Runs
          </GhostButton>
          <PrimaryButton onClick={onClose} data-testid="due-run-done-btn">Done</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}
